/* ---------------------------------------------------------------
   Normalizes whatever comes back in `video_link` from the backend
   into a URL that's actually safe/embeddable in an <iframe>.

   Handles:
   - Missing colon after the scheme ("https//..." -> "https://...")
     — a typo we've seen coming from the admin panel.
   - Missing scheme entirely ("youtube.com/..." -> "https://...").
   - YouTube "watch" and short "youtu.be" links -> /embed/{id}, since
     YouTube refuses to render those inside an iframe otherwise.
   - Vimeo "vimeo.com/{id}" links -> player.vimeo.com/video/{id}.
   - Anything else is returned as-is (assumed already embeddable).

   Returns "" if the value can't be turned into a usable http(s) URL
   (e.g. no video id at all), so callers can skip rendering the
   iframe instead of showing a broken/blocked frame.
------------------------------------------------------------------ */
export function toEmbeddableVideoUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  let url = raw.trim();
  if (!url) return "";

  // Fix "https//" / "http//" (missing colon).
  url = url.replace(/^(https?)\/\//i, "$1://");
  // Add a scheme if none is present at all.
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return "";
  }

  const host = parsed.hostname.replace(/^www\./, "").toLowerCase();

  // youtu.be/{id}
  if (host === "youtu.be") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    return id ? `https://www.youtube.com/embed/${id}` : "";
  }

  // youtube.com/watch?v={id} | /shorts/{id} | /embed/{id} | bare domain (no id)
  if (host === "youtube.com" || host === "m.youtube.com") {
    if (parsed.pathname.startsWith("/embed/")) return parsed.toString();
    if (parsed.pathname.startsWith("/shorts/")) {
      const id = parsed.pathname.split("/").filter(Boolean)[1];
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    const id = parsed.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}`;
    return ""; // bare "youtube.com" with no video id — nothing to embed
  }

  // vimeo.com/{id}
  if (host === "vimeo.com") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : "";
  }

  // Anything else: trust it's already a direct/embeddable URL.
  return parsed.toString();
}

/* ---------------------------------------------------------------
   Telegram Mini Apps run inside a locked-down WebView that blocks
   <iframe>s to third-party hosts like youtube.com/vimeo.com outright
   (Telegram Desktop shows its own "ERR_BLOCKED_BY_RESPONSE / Host
   ... is blocked" screen — this happens before our code, or even
   the target site's own headers, get a say). Embedding those hosts
   in an iframe inside a Mini App is a dead end.

   The supported pattern instead is: show a thumbnail/card and hand
   the URL to Telegram.WebApp.openLink(), which opens it in the
   user's browser (or Telegram's own in-app browser) outside the
   Mini App's restricted WebView.
------------------------------------------------------------------ */
const IFRAME_BLOCKED_HOSTS = new Set(["youtube.com", "youtu.be", "vimeo.com"]);

export function isIframeBlockedHost(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    return IFRAME_BLOCKED_HOSTS.has(host);
  } catch {
    return false;
  }
}

/* Extracts a YouTube video id (for building a thumbnail/watch link)
   from any of the common URL shapes. Returns "" if not YouTube. */
export function extractYouTubeId(rawUrl) {
  try {
    const url = new URL(
      /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl.replace(/^(https?)\/\//i, "$1://")}`
    );
    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "youtu.be") return url.pathname.split("/").filter(Boolean)[0] || "";
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (url.pathname.startsWith("/embed/")) return url.pathname.split("/").filter(Boolean)[1] || "";
      if (url.pathname.startsWith("/shorts/")) return url.pathname.split("/").filter(Boolean)[1] || "";
      return url.searchParams.get("v") || "";
    }
    return "";
  } catch {
    return "";
  }
}

/* Best-effort thumbnail for the click-to-play card. Only YouTube is
   supported without an extra network round-trip (its thumbnail CDN
   URLs are predictable); other hosts fall back to no image. */
export function getVideoThumbnail(rawUrl) {
  const id = extractYouTubeId(rawUrl);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

/* ---------------------------------------------------------------
   Detects a direct, playable video file/stream URL (own storage/CDN
   — mp4, webm, mov, or an HLS manifest) as opposed to a YouTube/
   Vimeo page link that needs an <iframe> (and therefore hits the
   Telegram iframe block). Query strings on signed/presigned URLs
   (e.g. "...video.mp4?X-Amz-Signature=...") are stripped before
   checking the extension.
------------------------------------------------------------------ */
export function isDirectVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const { pathname } = new URL(url);
    return /\.(mp4|webm|mov|m4v|m3u8)$/i.test(pathname);
  } catch {
    return false;
  }
}

/* Opens a URL the way a Telegram Mini App is supposed to open
   external links — via the WebApp bridge when available, so it
   escapes the restricted in-Mini-App WebView instead of trying
   (and failing) to load inline. Falls back to a normal new-tab
   open for plain-browser testing outside Telegram. */
export function openExternalLink(url) {
  if (!url) return;
  try {
    const tg = window?.Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(url, { try_instant_view: false });
      return;
    }
  } catch {}
  window.open(url, "_blank", "noopener,noreferrer");
}
